import React, { Component, createRef } from 'react';
import ReactFocusLock from 'react-focus-lock';
import { withAnalyticsEvents } from '@atlaskit/analytics-next';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import Popup, { type TriggerProps } from '@atlaskit/popup';
import { token } from '@atlaskit/tokens';
import { ff } from '@atlassian/jira-feature-flagging';
import { FormattedMessage, injectIntl } from '@atlassian/jira-intl';
import { mergeRefs } from '@atlassian/jira-merge-refs';
import {
	colors,
	CustomThemeButton,
	InlineDialog,
	Tooltip,
} from '@atlassian/jira-portfolio-3-common';
import { PRODUCT_ANALYTICS_EVENT_NAMES } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/analytics/types';
import {
	READONLY,
	REPORT,
} from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/app';
import { WARNING_TYPE_NUMBER_MAP } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/state/domain/warnings/types';
import {
	ContextualAnalyticsData,
	INLINE_DIALOG,
	fireUIAnalytics,
} from '@atlassian/jira-product-analytics-bridge';

import Content from './content';
import messages from './messages';
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles -- Ignored via go/DSP-18766
import * as styles from './styles.module.css';
import type { Props, InnerProps } from './types';

// eslint-disable-next-line jira/react/no-class-components
class WarningCenter extends Component<
	InnerProps,
	{
		isOpen: boolean;
		typeId: string | null;
	}
> {
	state = {
		isOpen: false,
		typeId: null,
	};

	buttonRef = createRef<HTMLButtonElement>();

	toggleWarningCenter = () => {
		if (this.state.isOpen) {
			this.setState({ isOpen: false }, () => {
				ff('jsw.roadmap.close-non-modal-dialogs') &&
					!this.state.isOpen &&
					this.buttonRef.current?.focus();
			});
			return;
		}
		this.setState({ isOpen: true });
		const { createAnalyticsEvent = null } = this.props;
		if (!createAnalyticsEvent) return;
		const [actionSubject, action] = PRODUCT_ANALYTICS_EVENT_NAMES.OPENED_WARNING_CENTER.split(' ');
		const analyticsEvent = createAnalyticsEvent({ action, actionSubject });
		fireUIAnalytics(analyticsEvent, {
			count: this.props.warningCount,
		});
	};

	close = () => this.setState({ isOpen: false });

	onClose = () => {
		this.setState({ isOpen: false }, () => {
			ff('jsw.roadmap.close-non-modal-dialogs') &&
				!this.state.isOpen &&
				this.buttonRef.current?.focus();
		});
	};

	handleTypeIdChange = (typeId: string | null) => {
		this.setState({ typeId });
		if (typeof typeId !== 'string') {
			return;
		}
		const { createAnalyticsEvent = null } = this.props;
		if (!createAnalyticsEvent) return;
		const [actionSubject, action] = PRODUCT_ANALYTICS_EVENT_NAMES.FILTERED_WARNING_TYPE.split(' ');
		const analyticsEvent = createAnalyticsEvent({ action, actionSubject });
		const warningType = WARNING_TYPE_NUMBER_MAP[typeId] || null;
		fireUIAnalytics(analyticsEvent, {
			count: this.props.warningCount,
			warningType,
		});
	};

	renderTriggerButton = (triggerProps?: TriggerProps) => {
		const { intl, mode, showWarning } = this.props;
		const { ref, ...restTriggerProps } = triggerProps || { ref: null };
		return (
			<div data-testid="portfolio-3-portfolio.app-simple-plans.top.title-bar.warnings.warning-centre-button">
				{/* in REPORT and READONLY mode, the Warnings button is always displayed with labels since there is enough space */}
				{mode !== REPORT && mode !== READONLY && (
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
					<div className={styles.warningsButtonIconOnly}>
						<Tooltip content={<FormattedMessage {...messages.warningsButtonLabel} />}>
							<CustomThemeButton
								appearance="default"
								{...(ff('jsw.roadmap.close-non-modal-dialogs') && {
									...restTriggerProps,
									ref: mergeRefs(this.buttonRef, ref),
									'aria-expanded': this.state.isOpen,
								})}
								onClick={this.toggleWarningCenter}
								iconBefore={
									<WarningIcon
										label={intl.formatMessage(messages.warningsButtonLabel)}
										primaryColor={
											showWarning
												? token('color.icon.warning', colors.Y300)
												: token('color.icon.subtle', colors.N200)
										}
										testId="portfolio-3-portfolio.app-simple-plans.top.title-bar.warnings.custom-theme-button"
									/>
								}
								{...(showWarning && {
									theme: (current, themeProps) => ({
										...current(themeProps),
										buttonStyles: {
											...current(themeProps).buttonStyles,
											background: token('color.background.warning', colors.Y75),
											'&:hover': {
												background: token('color.background.warning.hovered', colors.Y100),
											},
										},
									}),
								})}
							/>
						</Tooltip>
					</div>
				)}
				{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
				<div className={mode !== REPORT && mode !== READONLY ? styles.warningsButtonIconLabel : ''}>
					<CustomThemeButton
						appearance="default"
						{...(ff('jsw.roadmap.close-non-modal-dialogs') && {
							...restTriggerProps,
							ref: mergeRefs(this.buttonRef, ref),
							'aria-expanded': this.state.isOpen,
						})}
						onClick={this.toggleWarningCenter}
						{...(showWarning && {
							iconBefore: (
								<WarningIcon label="" primaryColor={token('color.icon.warning', colors.Y300)} />
							),
							theme: (current, themeProps) => ({
								...current(themeProps),
								buttonStyles: {
									...current(themeProps).buttonStyles,
									background: token('color.background.warning', colors.Y75),
									'&:hover': {
										background: token('color.background.warning.hovered', colors.Y100),
									},
								},
							}),
						})}
					>
						<FormattedMessage {...messages.warningsButtonLabel} />
					</CustomThemeButton>
				</div>
			</div>
		);
	};

	render() {
		const {
			warningCount,
			warnings,
			showWarning,
			toggleWarningByType,
			disabledWarnings,
			warningFilterValue,
			toggleWarningsFilter,
			issuesById,
			setSearchQuery,
			issueTypesById,
			projectsById,
			toggleShowWarning,
		} = this.props;

		if (ff('jsw.roadmap.close-non-modal-dialogs'))
			return (
				<Popup
					content={({ setInitialFocusRef }) => (
						<Content
							focusRef={setInitialFocusRef}
							disabledWarnings={disabledWarnings}
							issueTypesById={issueTypesById}
							issuesById={issuesById}
							onTypeIdChange={this.handleTypeIdChange}
							projectsById={projectsById}
							setSearchQuery={setSearchQuery}
							showWarning={showWarning}
							toggleShowWarning={toggleShowWarning}
							toggleWarningByType={toggleWarningByType}
							toggleWarningsFilter={toggleWarningsFilter}
							typeId={this.state.typeId}
							warningCount={warningCount}
							warningFilterValue={warningFilterValue}
							warnings={warnings}
						/>
					)}
					isOpen={this.state.isOpen}
					trigger={this.renderTriggerButton}
					shouldRenderToParent
					role="dialog"
					onClose={this.onClose}
					placement="bottom-end"
				/>
			);

		return (
			<InlineDialog
				isOpen={this.state.isOpen}
				content={
					<ReactFocusLock>
						<Content
							disabledWarnings={disabledWarnings}
							issueTypesById={issueTypesById}
							issuesById={issuesById}
							onTypeIdChange={this.handleTypeIdChange}
							projectsById={projectsById}
							setSearchQuery={setSearchQuery}
							showWarning={showWarning}
							toggleShowWarning={toggleShowWarning}
							toggleWarningByType={toggleWarningByType}
							toggleWarningsFilter={toggleWarningsFilter}
							typeId={this.state.typeId}
							warningCount={warningCount}
							warningFilterValue={warningFilterValue}
							warnings={warnings}
						/>
					</ReactFocusLock>
				}
				maxWidth={600}
				noPaddings
				onClose={this.close}
				placement="bottom-end"
			>
				{this.renderTriggerButton()}
			</InlineDialog>
		);
	}
}

const WarningCenterWithIntl = injectIntl(withAnalyticsEvents()(WarningCenter));

// eslint-disable-next-line jira/import/no-anonymous-default-export
export default (props: Props) => (
	<ContextualAnalyticsData sourceName="warningsCenter" sourceType={INLINE_DIALOG}>
		<WarningCenterWithIntl {...props} />
	</ContextualAnalyticsData>
);
